import type { TFunction } from "i18next";
import {
  NeedFigureListDto,
  NeedOverviewFigure,
  type NeedOverviewScopeDto,
  type NeedStatus,
  type ProcurementPhaseDto,
} from "../../autogen/bff-api";
import { bucketQuery } from "../../common/layout/statusBuckets";
import { urls } from "../../urls";
import { QUEUE_LINK } from "../home/panels/NeedsWaitingPanel";
import { PHASE_BUCKET } from "../projects/view-single/ProcurementSummary";
import type { PeriodRange } from "./procurementFigures";

export const NEED_FIGURE_ORDER: NeedOverviewFigure[] = [
  NeedOverviewFigure.Reported,
  NeedOverviewFigure.InQueue,
  NeedOverviewFigure.MedianQueueDays,
  NeedOverviewFigure.WithProcurerNothingStarted,
  NeedOverviewFigure.DeliverySoon,
  NeedOverviewFigure.BecameProcurement,
];

export const PERIOD_BOUND_NEED_FIGURES: NeedOverviewFigure[] = [
  NeedOverviewFigure.Reported,
  NeedOverviewFigure.MedianQueueDays,
  NeedOverviewFigure.BecameProcurement,
];

const LISTED_FIGURES: Record<NeedFigureListDto, NeedOverviewFigure> = {
  [NeedFigureListDto.Reported]: NeedOverviewFigure.Reported,
  [NeedFigureListDto.MedianQueueDays]: NeedOverviewFigure.MedianQueueDays,
  [NeedFigureListDto.WithProcurerNothingStarted]: NeedOverviewFigure.WithProcurerNothingStarted,
  [NeedFigureListDto.DeliverySoon]: NeedOverviewFigure.DeliverySoon,
  [NeedFigureListDto.BecameProcurement]: NeedOverviewFigure.BecameProcurement,
};

export const parseListedNeedFigure = (value: string | null): NeedFigureListDto | undefined =>
  Object.values(NeedFigureListDto).find((figure) => figure === value);

export const overviewFigureOf = (figure: NeedFigureListDto): NeedOverviewFigure => LISTED_FIGURES[figure];

export const needFigureLabel = (t: TFunction, figure: NeedOverviewFigure): string =>
  ({
    [NeedOverviewFigure.Reported]: t("Reported in the period"),
    [NeedOverviewFigure.InQueue]: t("Needs in the queue"),
    [NeedOverviewFigure.MedianQueueDays]: t("Median time in the queue"),
    [NeedOverviewFigure.WithProcurerNothingStarted]: t("With a buyer, no procurement"),
    [NeedOverviewFigure.DeliverySoon]: t("Desired delivery within 60 days"),
    [NeedOverviewFigure.BecameProcurement]: t("Became a procurement, mini-competition or call-off in the period"),
  })[figure];

export const needFigureDefinition = (t: TFunction, figure: NeedOverviewFigure): string =>
  ({
    [NeedOverviewFigure.Reported]: t(
      "Needs first sent in the period, whatever their status is now. A need sent back and sent in again counts once."
    ),
    [NeedOverviewFigure.InQueue]: t("Needs sent that nobody has taken yet, as of today."),
    [NeedOverviewFigure.MedianQueueDays]: t(
      "The median time from a need was sent until a buyer took it or rejected it, for the needs taken or rejected in the period. The days a need was back with the reporter are not counted."
    ),
    [NeedOverviewFigure.WithProcurerNothingStarted]: t(
      "Needs a buyer has taken, with no procurement, mini-competition or call-off started yet, as of today."
    ),
    [NeedOverviewFigure.DeliverySoon]: t(
      "Needs with nothing started whose desired delivery is within 60 days from today, or has already passed."
    ),
    [NeedOverviewFigure.BecameProcurement]: t(
      "Needs that became a procurement, mini-competition or call-off in the period and still are."
    ),
  })[figure];

export const needListLink = (figure: NeedOverviewFigure, range: PeriodRange): string =>
  figure === NeedOverviewFigure.InQueue
    ? QUEUE_LINK
    : `${urls.app.needs.list}?${new URLSearchParams({ figure, from: range.from, to: range.to })}`;

export const reportedNeedsLink = (range: PeriodRange, within: { status: NeedStatus } | { department: string }): string =>
  `${urls.app.needs.list}?${new URLSearchParams({ figure: NeedFigureListDto.Reported, from: range.from, to: range.to, ...within })}`;

export const procurementsFromNeedsLink = (phase: ProcurementPhaseDto, range: PeriodRange): string =>
  `${urls.app.sourcingEvents.viewAll}?${bucketQuery(PHASE_BUCKET[phase])}&${new URLSearchParams({
    needsSentFrom: range.from,
    needsSentTo: range.to,
  })}`;

export const plannedQuarterLink = (quarter: { from: string; to: string }): string =>
  `${urls.app.overview}?${new URLSearchParams({ tab: "procurement-plan", period: "custom", from: quarter.from, to: quarter.to })}`;

export const needScopeText = (t: TFunction, scope: NeedOverviewScopeDto): string =>
  scope.organizationWide ? String(t("the whole organisation")) : scope.departmentNames.join(", ");
